import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPpwrCountryExclusionRequest,
	updatePpwrCountryExclusionRequest,
} from 'redux/reducers/ppwr/reducer';
import { TPpwrCountryExclusion, TPpwrCountryExclusionBody } from 'redux/reducers/ppwr/types';
import { getAllCountries } from 'redux/reducers/settings/selectors';
import { TCountry } from 'redux/reducers/settings/types';
import { CountryFlag } from 'ui/CountryFlag';
import { COLORS } from 'services/constants/colors';

type TCountryExclusionDialogProps = {
	open: boolean;
	// Exclusion being edited, `null` when a new one is added.
	exclusion: TPpwrCountryExclusion | null;
	// Countries that already have an exclusion and cannot be picked again.
	excludedCountryIds: number[];
	onClose: () => void;
};

type TFormValues = {
	countryId: number | null;
	reason: string;
	reportsDoneBy: string;
	lucid: string;
	validTill: string;
};

const EMPTY_VALUES: TFormValues = {
	countryId: null,
	reason: '',
	reportsDoneBy: '',
	lucid: '',
	validTill: '',
};

const toFormValues = (exclusion: TPpwrCountryExclusion | null): TFormValues =>
	exclusion
		? {
				countryId: exclusion.countryId,
				reason: exclusion.reason ?? '',
				reportsDoneBy: exclusion.reportsDoneBy ?? '',
				lucid: exclusion.lucid ?? '',
				// The date input works with `YYYY-MM-DD`.
				validTill: exclusion.validTill?.slice(0, 10) ?? '',
			}
		: EMPTY_VALUES;

const emptyToNull = (value: string) => (value.trim() === '' ? null : value.trim());

export const CountryExclusionDialog: FC<TCountryExclusionDialogProps> = ({
	open,
	exclusion,
	excludedCountryIds,
	onClose,
}) => {
	const dispatch = useDispatch();
	const allCountries = useSelector(getAllCountries);
	const [values, setValues] = useState<TFormValues>(EMPTY_VALUES);
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		if (!open) return;
		setValues(toFormValues(exclusion));
		setSubmitted(false);
	}, [open, exclusion]);

	const countries = useMemo(
		() =>
			[...(allCountries ?? [])]
				.filter(
					(country) =>
						country.id === exclusion?.countryId ||
						(country.isEu && !excludedCountryIds.includes(country.id)),
				)
				.sort((a, b) => a.name.localeCompare(b.name)),
		[allCountries, exclusion, excludedCountryIds],
	);
	const selectedCountry = countries.find((country) => country.id === values.countryId) ?? null;
	const countryError = submitted && values.countryId === null;

	const setValue = (name: keyof TFormValues) => (value: TFormValues[typeof name]) =>
		setValues((prev) => ({ ...prev, [name]: value }));

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		setSubmitted(true);
		if (values.countryId === null) return;

		const body: TPpwrCountryExclusionBody = {
			countryId: values.countryId,
			reason: emptyToNull(values.reason),
			reportsDoneBy: emptyToNull(values.reportsDoneBy),
			lucid: emptyToNull(values.lucid),
			validTill: emptyToNull(values.validTill),
		};
		dispatch(
			exclusion
				? updatePpwrCountryExclusionRequest({ id: exclusion.id, body })
				: createPpwrCountryExclusionRequest(body),
		);
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 'bold' }}>
				{exclusion ? 'Edit Country Exclusion' : 'Add New Country Exclusion'}
			</DialogTitle>
			<form onSubmit={handleSubmit} noValidate>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
						<Autocomplete<TCountry>
							options={countries}
							value={selectedCountry}
							onChange={(_, country) => setValue('countryId')(country?.id ?? null)}
							getOptionLabel={(country) => country.name}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							renderOption={(props, country) => (
								<Box component="li" {...props} key={country.id} sx={{ gap: 1 }}>
									<CountryFlag countryCode={country.isoAlpha2} height={14} />
									{country.name} ({country.isoAlpha2})
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Country"
									required
									autoFocus={!exclusion}
									error={countryError}
									helperText={countryError ? 'Select a country' : ''}
								/>
							)}
						/>
						<TextField
							label="Reason"
							value={values.reason}
							onChange={(event) => setValue('reason')(event.target.value)}
							multiline
							minRows={3}
							fullWidth
						/>
						<TextField
							label="Reports Done By"
							value={values.reportsDoneBy}
							onChange={(event) => setValue('reportsDoneBy')(event.target.value)}
							fullWidth
						/>
						<TextField
							label="LUCID"
							value={values.lucid}
							onChange={(event) => setValue('lucid')(event.target.value)}
							fullWidth
						/>
						<TextField
							label="Valid Till"
							type="date"
							value={values.validTill}
							onChange={(event) => setValue('validTill')(event.target.value)}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
					<Button variant="outlined" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="contained" type="submit">
						{exclusion ? 'Save' : 'Add'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
